import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import {
  Form,
  Button,
  Radio,
  Dropdown,
  Grid,
  Label,
  Icon,
  Message
} from 'semantic-ui-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { userActions } from '../../actions/user.actions';
import { userConstants } from '../../constants/user.constants';
import { getRoleLabel, getBlockModeLabel, getJourneyModeLabel } from '../../helpers/user';
import moment from 'moment';
import momentDurationFormatSetup from "moment-duration-format";

// ForceBlocked is never reset to false once a temporary block expires (e.g. a holiday or a lunch-block
// notification) - only forceBlockExpiration says whether it's still in effect, matching the same check
// UserController.Status itself uses to decide whether to still report the user as blocked.
function isForceBlockActive(user) {
  return user.forceBlocked && (!user.forceBlockExpiration || moment(user.forceBlockExpiration).isAfter(moment()));
}

function forceBlockActionLabel(value) {
  switch (value) {
    case 1: return 'Logoff';
    case 2: return 'Bloqueio';
    case 3: return 'Não Bloquear';
    default: return 'Padrão Sistema';
  }
}

class UserFormScreen extends Component {
  constructor(props) {
    super(props);

    this.state = {
      user: {    
        name: '',
        username: '',
        windowsUsername: '',
        externalId: '',
        password: '',
        phone: '',
        email: '',
        department: '',
        role: '',
        allowSelfApproval: false,
        blockGroupChanges: false,
        blockMode: userConstants.BLOCK_MODE_AUTO,
        journeyMode: userConstants.JOURNEY_MODE_FLEXIBLE,
        status: 0,
        notificationEmails: '',
        expirationMode: 0,
        sendWelcomeEmail: false,
        active: true,
        immediateSuperiorId: '',
        mediateSuperiorId: '',
        workSchedule: [],
        groupIds: [],
      },
      errors: {},
      submitted: false
    };    
  }

  fetchManagers() {
    const { dispatch } = this.props;
    const { search } = this.state;
    dispatch(userActions.managers({ search, orderBy: "Name" }));
  }

  fetchGroups() {
    const { dispatch } = this.props;
    const { searchGroup } = this.state;
    dispatch(userActions.fetchGroups({ searchGroup, orderBy: "Name" }));
  }

  componentDidMount() {
    const { match, dispatch } = this.props;    
    
    this.handleSearchDebounced = _.debounce(function() {
      this.fetchManagers();
    }, 500);

    this.handleSearchGroupDebounced = _.debounce(function() {
      this.fetchGroups();
    }, 500);

    if (match.params.id) {
        dispatch(userActions.get(match.params.id))
            .then(() => {                
                const user = _.mapKeys(this.props.user, function (v, k) { return k[0].toLowerCase() + k.substr(1); });                                
                user.immediateSuperiorId = user.immediateSuperior ? user.immediateSuperior.Id : null;
                user.mediateSuperiorId = user.mediateSuperior ? user.mediateSuperior.Id : null;
                user.groupIds = user.groups?.map((group) => group.Id);                
                user.workSchedule.forEach((item) => {
                  var suffix = '_' + item.WeekDay;
                  user['start' + suffix] = moment.duration(item.StartTime.TotalMinutes, "minutes").format("HH:mm", { trim: false });
                  user['work' + suffix] = moment.duration(item.WorkMinutes, "minutes").format("HH:mm", { trim: false });
                  user['lunch' + suffix] = moment.duration(item.LunchMinutes, "minutes").format("HH:mm", { trim: false });
                  user['start_lunch' + suffix] = item.LunchStartTime ? moment.duration(item.LunchStartTime.TotalMinutes, "minutes").format("HH:mm", { trim: false }) : '';
                  user['start_tolerance' + suffix] = moment.duration(item.StartToleranceMinutes, "minutes").format("HH:mm", { trim: false });
                  user['end_tolerance' + suffix] = moment.duration(item.EndToleranceMinutes, "minutes").format("HH:mm", { trim: false });
                });
                
                this.setState({ user })
            });
    }    
    dispatch(userActions.managers());
    this.fetchGroups();
  }

  static get propTypes() { 
    return {       
        me: PropTypes.any,        
        user: PropTypes.any,
        loading: PropTypes.bool,
        managers: PropTypes.any,
        match: PropTypes.any
    }; 
  }

  simpleSearch(options, query) {    
    query = query.toLowerCase();        
    return options.filter(opt => {        
      return (opt.text && opt.text.toLowerCase().indexOf(query) >= 0) || (opt.description && opt.description.toLowerCase().indexOf(query) >= 0);
    });
  }

  handleChange(field, data) {        
    const { user } = this.state;    
    user[field] = data;    
    this.setState({ user });    
  }

  validate() {
    const { user } = this.state;
    
    let errors = {};
    let valid = true;

    if (!user.name || user.name.length == 0) {
      errors.name = "Preencha o nome";
      valid = false;
    }

    if (!user.username || user.username.length == 0) {
      errors.username = "Preencha o username";
      valid = false;
    }

    if (!user.email || user.email.length == 0) {
      errors.email = "Preencha o e-mail";
      valid = false;
    }

    if (!user.role || user.role == 0) {
      errors.role = "Preencha o perfil";
      valid = false;
    }

    this.setState({ errors });
    return valid;
  }

  roleOptions(isAdmin) {
    const roles = [
      {key: userConstants.ROLE_USER, value: userConstants.ROLE_USER, text: getRoleLabel(userConstants.ROLE_USER)},
      {key: userConstants.ROLE_MANAGER, value: userConstants.ROLE_MANAGER, text: getRoleLabel(userConstants.ROLE_MANAGER)},
      {key: userConstants.ROLE_APPROVER, value: userConstants.ROLE_APPROVER, text: getRoleLabel(userConstants.ROLE_APPROVER)},
      {key: userConstants.ROLE_SERVICE_DESK, value: userConstants.ROLE_SERVICE_DESK, text: getRoleLabel(userConstants.ROLE_SERVICE_DESK)},
      {key: userConstants.ROLE_USER_MANAGER, value: userConstants.ROLE_USER_MANAGER, text: getRoleLabel(userConstants.ROLE_USER_MANAGER)},
      {key: userConstants.ROLE_AUDIT, value: userConstants.ROLE_AUDIT, text: getRoleLabel(userConstants.ROLE_AUDIT)},
    ];

    if (isAdmin) {
      roles.push({key: userConstants.ROLE_ADMIN, value: userConstants.ROLE_ADMIN, text: getRoleLabel(userConstants.ROLE_ADMIN)});
    }

    return roles;
  }

  blockModeOptions() {
    return [
      {key: userConstants.BLOCK_MODE_AUTO, value: userConstants.BLOCK_MODE_AUTO, text: getBlockModeLabel(userConstants.BLOCK_MODE_AUTO)},
      {key: userConstants.BLOCK_MODE_ENABLED, value: userConstants.BLOCK_MODE_ENABLED, text: getBlockModeLabel(userConstants.BLOCK_MODE_ENABLED)},
      {key: userConstants.BLOCK_MODE_DISABLED, value: userConstants.BLOCK_MODE_DISABLED, text: getBlockModeLabel(userConstants.BLOCK_MODE_DISABLED)},
    ]
  }

  journeyModeOptions() {
    return [
      {key: userConstants.JOURNEY_MODE_FLEXIBLE, value: userConstants.JOURNEY_MODE_FLEXIBLE, text: getJourneyModeLabel(userConstants.JOURNEY_MODE_FLEXIBLE)},
      {key: userConstants.JOURNEY_MODE_RIGID, value: userConstants.JOURNEY_MODE_RIGID, text: getJourneyModeLabel(userConstants.JOURNEY_MODE_RIGID)},
    ]
  }

  handleUserSearchChange(search) {    
    this.setState({ search });
    this.handleSearchDebounced();
  }

  handleGroupSearchChange(searchGroup) {    
    this.setState({ searchGroup });
    this.handleSearchGroupDebounced();
  }

  copyWorkSchedule() {
      const { user } = this.state;      
      for (let i = 2; i < 6; i++) {
        const suffix = '_' + i;
        user['start' + suffix] = user['start_1'];
        user['work' + suffix] = user['work_1'];
        user['lunch' + suffix] = user['lunch_1'];
        user['start_lunch' + suffix] = user['start_lunch_1'],
        user['start_tolerance' + suffix] = user['start_tolerance_1'];
        user['end_tolerance' + suffix] = user['end_tolerance_1'];
      }      
      this.setState({ user });
  }

  handleSubmit() {
    if (!this.validate()) {
      return;
    }

    const { dispatch } = this.props;
    const { user } = this.state;    
    
    const workSchedule = [];
    for (let i = 0; i < 7; i++) {
      const suffix = '_' + i;
      if (user['start' + suffix]) {
        workSchedule.push({ 
          WeekDay: i,
          StartTime: user['start' + suffix],
          WorkMinutes: moment.duration(user['work' + suffix]).asMinutes(),
          LunchMinutes: moment.duration(user['lunch' + suffix]).asMinutes(),
          LunchStartTime: user['start_lunch' + suffix],
          StartToleranceMinutes: moment.duration(user['start_tolerance' + suffix]).asMinutes(),
          EndToleranceMinutes: moment.duration(user['end_tolerance' + suffix]).asMinutes(),
        });
      }    
    }    

    const payload = {
      name: user.name,
      username: user.username,
      windowsUsername: user.windowsUsername,
      domain: user.domain,
      email: user.email,
      externalId: user.externalId,
      department: user.department,
      role: user.role,
      blockMode: user.blockMode,
      journeyMode: user.journeyMode,
      expirationMode: user.expirationMode,
      documentNumber: user.documentNumber,
      password: user.password || null,
      immediateSuperiorId: user.immediateSuperiorId,
      mediateSuperiorId: user.mediateSuperiorId,
      active: user.active,
      allowSelfApproval: user.allowSelfApproval,
      blockGroupChanges: user.blockGroupChanges,
      status: user.status,
      sendWelcomeEmail: user.sendWelcomeEmail,
      notificationEmails: user.notificationEmails,
      groupIds: user.groupIds,
      workSchedule
    };

    if (user.id) {
        dispatch(userActions.update(user.id, payload));
    } else {
        dispatch(userActions.create(payload));
    }
  }

  render() {    
    const { user, errors } = this.state;        
    const { managers, me, usersLoading, groups } = this.props;    

    // Check Permission
    if (me && me.permissions && !me.permissions.includes("user.create")) {
      window.location.href = "/";
      return;
    }
    
    let groupOptions = [];             
    
    if (groups && groups.Results) {      
      groupOptions = groups.Results.map(item => {
        return { key: item.Id, value: item.Id, text: item.Name }
      });
    }
    
    let managerOptions = [];     
    
    if (managers && managers.Results) {      
      managerOptions = managers.Results.map(item => {
        return { key: item.Id, value: item.Id, text: item.Name, description: item.Email, username: item.Username }
      });

      if (user.immediateSuperior) {
        managerOptions.push({key: user.immediateSuperior.Id, value: user.immediateSuperior.Id, text: user.immediateSuperior.Name, description: user.immediateSuperior.Email, username: user.immediateSuperior.Username});
      }

      if (user.mediateSuperior) {
        managerOptions.push({key: user.mediateSuperior.Id, value: user.mediateSuperior.Id, text: user.mediateSuperior.Name, description: user.mediateSuperior.Email, username: user.mediateSuperior.Username});
      }
    }

    const expirationModeOptions = [
      { key: 0, value: 0, text: "Padrão Sistema" },
      { key: 1, value: 1, text: "Logoff" },
      { key: 2, value: 2, text: "Bloqueio" },
      { key: 3, value: 3, text: "Não Bloquear" },
    ];

    const statusOptions = [
      { key: 0, value: 0, text: "Trabalhando" },
      { key: 1, value: 1, text: "Afastado" },      
    ];
    
    return (  
      <Fragment>
      <h1>Usuário</h1>
      {isForceBlockActive(user) &&
        <Message negative icon>
          <Icon name='lock' />
          <Message.Content>
            <Message.Header>Bloqueio forçado ativo</Message.Header>
            Ação: {forceBlockActionLabel(user.forceBlockAction)} - {user.forceBlockExpiration
              ? `Até ${moment(user.forceBlockExpiration).format('DD/MM/YYYY HH:mm')}`
              : 'Sem prazo definido'}. Este status é controlado automaticamente pelo sistema e não pode ser alterado aqui.
          </Message.Content>
        </Message>}
      <Form loading={this.props.loading} onSubmit={() => this.handleSubmit()}>
        <Form.Input required error={errors.name} label='Nome' value={user.name} name='name' type='text' onChange={(e, data) => this.handleChange("name", data.value)} />
        { errors.name && <Label color='red'>{errors.name}</Label>}

        <Form.Input required error={errors.username} label='Username' value={user.username} name='username' type='text' onChange={(e, data) => this.handleChange("username", data.value)} />
        { errors.username && <Label color='red'>{errors.username}</Label>}

        <Form.Input error={errors.domain} label='Domínio (somente se diferente do padrão)' value={user.domain} name='domain' type='text' onChange={(e, data) => this.handleChange("domain", data.value)} />
        { errors.domain && <Label color='red'>{errors.domain}</Label>}

        <Form.Input error={errors.windowsUsername} label='Usuário Windows (somente se diferente de "Username")' value={user.windowsUsername} name='windowsUsername' type='text' onChange={(e, data) => this.handleChange("windowsUsername", data.value)} />
        { errors.windowsUsername && <Label color='red'>{errors.windowsUsername}</Label>}

        <Form.Input error={errors.externalId} label='ID Externo (para integração)' value={user.externalId} name='externalId' type='text' onChange={(e, data) => this.handleChange("externalId", data.value)} />
        { errors.externalId && <Label color='red'>{errors.externalId}</Label>}

        <Form.Input error={errors.documentNumber} label='PIS/Documento' value={user.documentNumber} name='documentNumber' type='text' onChange={(e, data) => this.handleChange("documentNumber", data.value)} />
        { errors.documentNumber && <Label color='red'>{errors.documentNumber}</Label>}

        <Form.Input required error={errors.email} label='E-Mail' value={user.email} name='email' type='text' onChange={(e, data) => this.handleChange("email", data.value)} />
        { errors.email && <Label color='red'>{errors.email}</Label>}

        <Form.Input error={errors.department} label='Departamento' value={user.department} name='department' type='text' onChange={(e, data) => this.handleChange("department", data.value)} />
        { errors.department && <Label color='red'>{errors.department}</Label>}
        
        <Form.Input error={errors.password} disabled={user.sendWelcomeEmail} label='Senha' name='password' type='password' onChange={(e, data) => this.handleChange("password", data.value)} placeholder='Deixe em branco para manter'/>
        { errors.password && <Label color='red'>{errors.password}</Label>}
        <Form.Input>
            <Radio toggle checked={user.sendWelcomeEmail} label='Gerar nova senha e enviar por e-mail' onChange={(e, data) => this.handleChange("sendWelcomeEmail", data.checked)} />
        </Form.Input>        

        
        <Form.Field required>
            <label>Perfil</label>
            <Dropdown
              placeholder='Selecione'
              fluid
              label='Perfil'
              selection
              value={user.role} 
              onChange={(e, data) => this.handleChange("role", data.value)}
              options={this.roleOptions(me?.Role === userConstants.ROLE_ADMIN)}
            />
            { errors.role && <Label basic pointing color='red'>{errors.role}</Label>}
        </Form.Field>       

        <Form.Field required>
            <label>Modo Bloqueio</label>
            <Dropdown
              placeholder='Selecione'
              fluid
              label='Modo de Bloqueio'
              selection
              value={user.blockMode} 
              onChange={(e, data) => this.handleChange("blockMode", data.value)}
              options={this.blockModeOptions()}
            />
            { errors.blockMode && <Label basic pointing color='red'>{errors.blockMode}</Label>}
        </Form.Field>         

        <Form.Field required>
            <label>Modo Jornada</label>
            <Dropdown
              placeholder='Selecione'
              fluid
              label='Modo de Jornada'
              selection
              value={user.journeyMode} 
              onChange={(e, data) => this.handleChange("journeyMode", data.value)}
              options={this.journeyModeOptions()}
            />
            { errors.blockMode && <Label basic pointing color='red'>{errors.blockMode}</Label>}
        </Form.Field>          

        <Form.Field required>
            <label>Ação Término Sessão</label>
            <Dropdown
              placeholder='Selecione'
              fluid
              label='Ação Término Sessão'
              selection
              value={user.expirationMode} 
              onChange={(e, data) => this.handleChange("expirationMode", data.value)}
              options={expirationModeOptions}
            />
            { errors.expirationMode && <Label basic pointing color='red'>{errors.expirationMode}</Label>}
        </Form.Field> 

        <Form.Field>
            <label>Grupos</label>
            <Dropdown
              placeholder='Digite nome do grupo'
              fluid
              multiple
              selection
              loading={usersLoading}
              onChange={(e, data) => this.handleChange("groupIds", data.value)}                            
              value={user.groupIds || []}
              options={groupOptions}
            />
            { errors.groupIds && <Label basic pointing  color='red'>{errors.groupIds}</Label>}
          </Form.Field>         

        <Form.Field>
            <label>Superior Imediato</label>
            <Dropdown
            placeholder='Superior Imediato - busque por nome, e-mail ou username'
            fluid            
            selection        
            clearable    
            loading={usersLoading}
            onChange={(e, data) => this.handleChange("immediateSuperiorId", data.value)}
            onSearchChange={(e, data) => this.handleUserSearchChange(data.searchQuery)}                        
            search={(opt, qry) => this.simpleSearch(opt, qry)}
            value={user.immediateSuperiorId} 
            options={managerOptions}
          />                        
            { errors.immediateSuperiorId && <Label basic pointing color='red'>{errors.immediateSuperiorId}</Label>}
        </Form.Field>          

        <Form.Field>
            <label>Superior Mediato</label>
            <Dropdown
            placeholder='Superior Mediato - busque por nome, e-mail ou username'
            fluid    
            clearable        
            selection            
            loading={usersLoading}
            onChange={(e, data) => this.handleChange("mediateSuperiorId", data.value)}
            onSearchChange={(e, data) => this.handleUserSearchChange(data.searchQuery)}                        
            search={(opt, qry) => this.simpleSearch(opt, qry)}
            value={user.mediateSuperiorId} 
            options={managerOptions}
          />                        
            { errors.mediateSuperiorId && <Label basic pointing color='red'>{errors.mediateSuperiorId}</Label>}
        </Form.Field>

        <Form.Field>
            <label>Situação</label>
            <Dropdown
              placeholder='Selecione'
              fluid
              label='Situação'
              selection
              value={user.status} 
              onChange={(e, data) => this.handleChange("status", data.value)}
              options={statusOptions}
            />
            { errors.status && <Label basic pointing color='red'>{errors.status}</Label>}
        </Form.Field>

        <Form.Input error={errors.notificationEmails} label='E-Mails Notificação Aprovação (separados por ,)' value={user.notificationEmails} name='notificationEmails' type='text' onChange={(e, data) => this.handleChange("notificationEmails", data.value)} />
        { errors.notificationEmails && <Label color='red'>{errors.notificationEmails}</Label>}
               
        <Form.Input>
            <Radio toggle checked={user.allowSelfApproval} label='Permitir Auto-Aprovação' onChange={(e, data) => this.handleChange("allowSelfApproval", data.checked)} />
        </Form.Input>

        <Form.Input>
            <Radio toggle checked={user.blockGroupChanges} label='Bloquear Alterações por Grupo ou Integração' onChange={(e, data) => this.handleChange("blockGroupChanges", data.checked)} />
        </Form.Input>
        
        <Form.Input>
            <Radio toggle checked={user.active} label='Habilitado' onChange={(e, data) => this.handleChange("active", data.checked)} />
        </Form.Input>

        <h3>Jornada de Trabalho</h3>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>Dia da Semana</Grid.Column>
            <Grid.Column width={2}>Entrada</Grid.Column>
            <Grid.Column width={2}>Jornada</Grid.Column>
            <Grid.Column width={2}>Início Intervalo (opcional)</Grid.Column>
            <Grid.Column width={2}>Intervalo</Grid.Column>
            <Grid.Column width={2}>Tolerância Entrada</Grid.Column>            
            <Grid.Column width={2}>Tolerância Saída</Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              Segunda-feira
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_1} placeholder='09:00' name='start_1' type='text' onChange={(e, data) => this.handleChange("start_1", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_1} name='work_1' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_1", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_1} name='start_lunch_1' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_1", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_1} name='lunch_1' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_1", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_1} name='start_tolerance_1' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_1", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_1} name='end_tolerance_1' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_1", data.value)} />              
            </Grid.Column>
            <Grid.Column width={1}>
              <Button circular icon='copy' type='button' size='mini'  title='Copiar para dias úteis' onClick={() => this.copyWorkSchedule()}></Button>              
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              Terça-feira
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_2} placeholder='09:00' name='start_2' type='text' onChange={(e, data) => this.handleChange("start_2", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_2} name='work_2' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_2", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_2} name='start_lunch_2' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_2", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_2} name='lunch_2' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_2", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_2} name='start_tolerance_2' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_2", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_2} name='end_tolerance_2' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_2", data.value)} />
            </Grid.Column>
          </Grid.Row>   

          <Grid.Row>
            <Grid.Column width={3}>
              Quarta-feira
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_3} placeholder='09:00' name='start_3' type='text' onChange={(e, data) => this.handleChange("start_3", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_3} name='work_3' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_3", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_3} name='start_lunch_3' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_3", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_3} name='lunch_3' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_3", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_3} name='start_tolerance_3' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_3", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_3} name='end_tolerance_3' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_3", data.value)} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              Quinta-feira
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_4} placeholder='09:00' name='start_4' type='text' onChange={(e, data) => this.handleChange("start_4", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_4} name='work_4' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_4", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_4} name='start_lunch_4' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_4", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_4} name='lunch_4' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_4", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_4} name='start_tolerance_4' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_4", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_4} name='end_tolerance_4' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_4", data.value)} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              Sexta-feira
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_5} placeholder='09:00' name='start_5' type='text' onChange={(e, data) => this.handleChange("start_5", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_5} name='work_5' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_5", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_5} name='start_lunch_5' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_5", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_5} name='lunch_5' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_5", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_5} name='start_tolerance_5' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_5", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_5} name='end_tolerance_5' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_5", data.value)} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              Sábado
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_6} placeholder='09:00' name='start_6' type='text' onChange={(e, data) => this.handleChange("start_6", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_6} name='work_6' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_6", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_6} name='start_lunch_6' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_6", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_6} name='lunch_6' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_6", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_6} name='start_tolerance_6' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_6", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_6} name='end_tolerance_6' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_6", data.value)} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={3}>
              Domingo
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_0} placeholder='09:00' name='start_0' type='text' onChange={(e, data) => this.handleChange("start_0", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.work_0} name='work_0' type='text' placeholder='08:00' onChange={(e, data) => this.handleChange("work_0", data.value)} />
            </Grid.Column>            
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_lunch_0} name='start_lunch_0' placeholder='12:00'  type='text' onChange={(e, data) => this.handleChange("start_lunch_0", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.lunch_0} name='lunch_0' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("lunch_0", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.start_tolerance_0} name='start_tolerance_0' placeholder='01:00' type='text' onChange={(e, data) => this.handleChange("start_tolerance_0", data.value)} />
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input size='mini' value={user.end_tolerance_0} name='end_tolerance_0' type='text' placeholder='00:10' onChange={(e, data) => this.handleChange("end_tolerance_0", data.value)} />
            </Grid.Column>
          </Grid.Row>
          
        </Grid>
                
        <Button primary type='submit'>{user.id ? 'Alterar' : 'Criar'} Usuário</Button>
      </Form>
    </Fragment>
    );
  }
}

function mapStateToProps(state) {
  const { users } = state;  
  let props = {
    me: users.me || null,    
    usersLoading: users.loading,        
    managers: users.managers || null,
    groups: users.groups || null,
    loading: users.loading    
  };  

  if (users && users.item) {    
      var user = users.item;      
      props.user = user;
  }  
  
  return props;
}

const connectedUserFormScreen = connect(mapStateToProps)(UserFormScreen);
export { connectedUserFormScreen as UserFormScreen };
